class LpCustomHeader extends HTMLElement {
  #abort = new AbortController();
  #promoObserver = null;
  #barObserver = null;
  #headerObserver = null;
  #atcObserver = null;
  #atcMutationObserver = null;
  #lastScrollY = 0;
  #scrollDownDistance = 0;
  #resizeHandler = null;
  #isBarPinned = false;
  #cachedMaxScroll = 0;
  #cachedBarHeight = 0;
  #cachedPromoHeight = 0;
  #collapseMinScrollY = 120;
  #collapseScrollDistance = 600;
  #expandScrollDelta = 10;
  #scrollThresholdBuffer = 0.5;

  connectedCallback() {
    const signal = this.#abort.signal;

    this.#applyBarMode();
    this.#measureHeights();
    this.#refreshScrollMetrics();
    this.#initHeaderMeasurement();
    this.#initBarPinning();
    this.#initOfferHeaders();
    this.#syncLayoutOffsets();
    this.#syncBottomOfferPadding();
    this.#initScrollHandlers(signal);
    this.#initAtcSuppression(signal);
    this.#initScrollCtas(signal);

    this.#resizeHandler = () => {
      this.#applyBarMode();
      this.#measureHeights();
      this.#refreshScrollMetrics();
      this.#initBarPinning();

      if (this.#getBarPosition() !== "fixed_collapse") {
        this.classList.remove("is-scroll-collapsed");
        this.#scrollDownDistance = 0;
      }

      this.#initOfferHeaders();
      this.#updateScrollReveal();
      this.#syncLayoutOffsets();
    };

    window.addEventListener("resize", this.#resizeHandler, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#promoObserver?.disconnect();
    this.#barObserver?.disconnect();
    this.#headerObserver?.disconnect();
    this.#atcObserver?.disconnect();
    this.#atcMutationObserver?.disconnect();
    document.body.classList.remove("lp-custom-header-active", "lp-offer-bottom-active");
    document.body.style.removeProperty("--lp-header-stack-height");
    document.body.style.removeProperty("--lp-offer-bottom-offset");
  }

  #isDesktop() {
    return window.matchMedia("(min-width: 741px)").matches;
  }

  #getBarPosition() {
    const position = this.#isDesktop() ? this.dataset.barDesktopPosition : this.dataset.barMobilePosition;

    return position || "fixed";
  }

  #shouldPinBar() {
    const position = this.#getBarPosition();
    return position === "fixed" || position === "fixed_collapse";
  }

  #barUsesScrollThreshold() {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    return bar?.classList.contains("has-scroll-threshold") ?? false;
  }

  #getBarScrollThresholds() {
    if (this.#isDesktop()) {
      return {
        show: this.#parseScrollThreshold(this.dataset.barScrollThreshold),
        hide: this.#parseScrollThreshold(this.dataset.barHideScrollThreshold),
      };
    }

    return {
      show: this.#parseScrollThreshold(this.dataset.barMobileScrollThreshold),
      hide: this.#parseScrollThreshold(this.dataset.barMobileHideScrollThreshold),
    };
  }

  #getOfferScrollThresholds(offer) {
    if (this.#isDesktop()) {
      return {
        show: this.#parseScrollThreshold(offer.dataset.scrollThreshold),
        hide: this.#parseScrollThreshold(offer.dataset.hideScrollThreshold),
      };
    }

    return {
      show: this.#parseScrollThreshold(offer.dataset.mobileScrollThreshold),
      hide: this.#parseScrollThreshold(offer.dataset.mobileHideScrollThreshold),
    };
  }

  #getOfferHideWhenAtc(offer) {
    return this.#isDesktop() ? offer.dataset.hideWhenAtc === "true" : offer.dataset.mobileHideWhenAtc === "true";
  }

  #getOfferMode(offer) {
    return this.#isDesktop() ? offer.dataset.desktopMode : offer.dataset.mobileMode;
  }

  #applyBarMode() {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    if (!bar) return;

    const positionClass = this.#getBarPosition().replace(/_/g, "-");

    bar.classList.remove("s-lp-custom-header__bar--static", "s-lp-custom-header__bar--fixed", "s-lp-custom-header__bar--fixed-collapse");
    bar.classList.add(`s-lp-custom-header__bar--${positionClass}`);
  }

  #measureHeights() {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    const promo = this.querySelector(".s-lp-custom-header__promo");

    if (bar) {
      this.#cachedBarHeight = bar.offsetHeight;
    }

    this.#cachedPromoHeight = promo?.offsetHeight || 0;
  }

  #getStackHeight() {
    return this.#cachedPromoHeight + this.#cachedBarHeight;
  }

  #initHeaderMeasurement() {
    const header = this.querySelector(".s-lp-custom-header");
    if (!header) return;

    this.#headerObserver = new ResizeObserver(() => {
      this.#measureHeights();
      this.#syncLayoutOffsets();
    });

    this.#headerObserver.observe(header);
  }

  #refreshScrollMetrics() {
    this.#cachedMaxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  #initBarPinning() {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    const spacer = this.querySelector(".s-lp-custom-header__bar-spacer");
    if (!bar || !spacer) return;

    this.#promoObserver?.disconnect();
    this.#promoObserver = null;
    this.#barObserver?.disconnect();
    this.#barObserver = null;

    if (this.#barUsesScrollThreshold() || !this.#shouldPinBar()) {
      this.#setBarPinned(false);
      return;
    }

    const promo = this.querySelector(".s-lp-custom-header__promo");

    if (!promo) {
      this.#setBarPinned(true);
    } else {
      this.#promoObserver = new IntersectionObserver(
        ([entry]) => {
          this.#setBarPinned(!entry.isIntersecting);
        },
        { threshold: 0 },
      );
      this.#promoObserver.observe(promo);
    }

    this.#barObserver = new ResizeObserver(() => {
      this.#measureHeights();

      if (this.#isBarPinned) {
        this.#syncBarSpacer();
        this.#syncLayoutOffsets();
      }
    });
    this.#barObserver.observe(bar);
  }

  #setBarPinned(pinned) {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    if (!bar || this.#isBarPinned === pinned) return;

    this.#isBarPinned = pinned;

    if (pinned) {
      this.#measureHeights();
      bar.classList.add("is-pinned");
      this.#syncBarSpacer();
    } else {
      bar.classList.remove("is-pinned");
      this.#syncBarSpacer();
    }

    this.classList.toggle("is-bar-pinned", pinned);
    this.#syncLayoutOffsets();
  }

  #syncBarSpacer() {
    const spacer = this.querySelector(".s-lp-custom-header__bar-spacer");
    if (!spacer) return;

    if (this.#isBarPinned) {
      spacer.style.height = `${this.#cachedBarHeight || 0}px`;
      spacer.hidden = false;
      return;
    }

    spacer.hidden = true;
    spacer.style.height = "";
  }

  #getPromoBottomOffset() {
    const promo = this.querySelector(".s-lp-custom-header__promo");
    if (!promo) return 0;

    const promoRect = promo.getBoundingClientRect();
    return Math.max(0, Math.round(promoRect.bottom));
  }

  #syncLayoutOffsets() {
    const header = this.querySelector(".s-lp-custom-header");
    const bar = this.querySelector(".s-lp-custom-header__bar");
    if (!header || !bar) return;

    const barHeight = this.#cachedBarHeight || bar.offsetHeight;
    const stackHeight = this.#getStackHeight();
    const promoBottom = this.#getPromoBottomOffset();
    const offerOffset = promoBottom > 0 ? promoBottom + barHeight : barHeight;

    this.style.setProperty("--lp-bar-top-offset", `${promoBottom}px`);
    this.style.setProperty("--lp-header-offset", `${offerOffset}px`);
    this.style.setProperty("--lp-header-stack-height", `${stackHeight}px`);

    document.body.classList.add("lp-custom-header-active");
    document.body.style.setProperty("--lp-header-stack-height", `${stackHeight}px`);

    if (this.#barUsesScrollThreshold()) {
      header.style.minHeight = `${stackHeight}px`;
      return;
    }

    header.style.minHeight = "";
  }

  #parseScrollThreshold(value) {
    const parsed = Number.parseFloat(value);
    if (Number.isNaN(parsed) || parsed <= 0) return 0;
    return Math.min(100, Math.max(0, parsed));
  }

  #getScrollProgress() {
    if (this.#cachedMaxScroll <= 0) return 100;
    return (window.scrollY / this.#cachedMaxScroll) * 100;
  }

  #isScrollPercentVisible(showThreshold, hideThreshold, wasRevealed) {
    const progress = this.#getScrollProgress();
    let showMet;

    if (showThreshold <= 0) {
      showMet = true;
    } else if (wasRevealed) {
      showMet = progress >= showThreshold - this.#scrollThresholdBuffer;
    } else {
      showMet = progress >= showThreshold;
    }

    if (!showMet) return false;

    if (hideThreshold > 0 && hideThreshold <= showThreshold) return false;

    if (hideThreshold > 0) {
      if (wasRevealed) {
        if (progress >= hideThreshold) return false;
      } else if (progress >= hideThreshold - this.#scrollThresholdBuffer) {
        return false;
      }
    }

    return true;
  }

  #setScrollRevealed(element, shouldReveal) {
    const wasRevealed = element.classList.contains("is-scroll-revealed");
    if (wasRevealed === shouldReveal) return false;

    element.classList.toggle("is-scroll-revealed", shouldReveal);
    return true;
  }

  #updateScrollReveal() {
    const bar = this.querySelector(".s-lp-custom-header__bar");
    const barThresholds = this.#getBarScrollThresholds();
    const hadScrollThreshold = this.#barUsesScrollThreshold();

    if (bar) {
      bar.classList.toggle("has-scroll-threshold", barThresholds.show > 0 || barThresholds.hide > 0);
      this.#setScrollRevealed(bar, this.#isScrollPercentVisible(barThresholds.show, barThresholds.hide, bar.classList.contains("is-scroll-revealed")));
    }

    this.querySelectorAll(".s-lp-custom-header__offer").forEach((offer) => {
      const thresholds = this.#getOfferScrollThresholds(offer);
      offer.classList.toggle("has-scroll-threshold", thresholds.show > 0 || thresholds.hide > 0);
      this.#setScrollRevealed(offer, this.#isScrollPercentVisible(thresholds.show, thresholds.hide, offer.classList.contains("is-scroll-revealed")));
      offer.setAttribute("data-atc-hide-enabled", this.#getOfferHideWhenAtc(offer) ? "true" : "false");
    });

    if (hadScrollThreshold !== this.#barUsesScrollThreshold()) {
      this.#initBarPinning();
    }

    this.#syncLayoutOffsets();
    this.#syncBottomOfferPadding();
    this.#syncOfferAtcHide();
    this.#markScrollReady();
  }

  #markScrollReady() {
    if (this.classList.contains("is-scroll-ready")) return;

    requestAnimationFrame(() => {
      this.classList.add("is-scroll-ready");
    });
  }

  #initOfferHeaders() {
    this.querySelectorAll(".s-lp-custom-header__offer").forEach((offer) => {
      this.#applyOfferMode(offer);
    });
    this.#syncOfferAtcHide();
  }

  #applyOfferMode(offer) {
    const mode = this.#getOfferMode(offer) || "static";

    offer.classList.remove("s-lp-custom-header__offer--static", "s-lp-custom-header__offer--fixed-top", "s-lp-custom-header__offer--fixed-bottom");

    if (mode === "static") {
      offer.classList.add("s-lp-custom-header__offer--static");
      return;
    }

    offer.classList.add(mode.includes("top") ? "s-lp-custom-header__offer--fixed-top" : "s-lp-custom-header__offer--fixed-bottom");

    const thresholds = this.#getOfferScrollThresholds(offer);
    offer.classList.toggle("has-scroll-threshold", thresholds.show > 0 || thresholds.hide > 0);
    offer.setAttribute("data-atc-hide-enabled", this.#getOfferHideWhenAtc(offer) ? "true" : "false");
  }

  #syncBottomOfferPadding() {
    let bottomHeight = 0;

    this.querySelectorAll(".s-lp-custom-header__offer").forEach((offer) => {
      const mode = this.#getOfferMode(offer);

      if (mode.includes("bottom") && offer.classList.contains("is-scroll-revealed") && !offer.classList.contains("has-scroll-threshold")) {
        bottomHeight = Math.max(bottomHeight, offer.offsetHeight);
      }
    });

    document.body.classList.toggle("lp-offer-bottom-active", bottomHeight > 0);

    if (bottomHeight > 0) {
      document.body.style.setProperty("--lp-offer-bottom-offset", `${bottomHeight}px`);
    } else {
      document.body.style.removeProperty("--lp-offer-bottom-offset");
    }
  }

  #initScrollHandlers(signal) {
    this.#lastScrollY = window.scrollY;
    this.#scrollDownDistance = 0;

    window.addEventListener(
      "scroll",
      () => {
        const currentY = window.scrollY;
        const delta = currentY - this.#lastScrollY;

        if (delta > 0) {
          this.#scrollDownDistance += delta;

          if (this.#getBarPosition() === "fixed_collapse" && this.#scrollDownDistance >= this.#collapseScrollDistance && currentY > this.#collapseMinScrollY) {
            this.classList.add("is-scroll-collapsed");
          }
        } else if (delta < -this.#expandScrollDelta) {
          this.#scrollDownDistance = 0;
          this.classList.remove("is-scroll-collapsed");
        }

        this.#lastScrollY = currentY;
        this.#syncLayoutOffsets();
        this.#updateScrollReveal();
      },
      { passive: true, signal },
    );

    this.#updateScrollReveal();
  }

  #getAtcHideTargets() {
    const targets = [];
    const product =
      document.getElementById("product-section") ||
      document.querySelector(".shopify-section--lp-main-product .s-main-product__container") ||
      document.querySelector(".shopify-section--lp-main-product s-main-product") ||
      document.querySelector(".shopify-section--lp-main-product .s-main-product") ||
      document.querySelector(".shopify-section--lp-main-product");

    if (product) targets.push(product);

    document
      .querySelectorAll(
        ".shopify-section--lp-main-product .js-subscribtion__add-to-cart, .shopify-section--lp-main-product .c-subscribtion__add-to-cart, .shopify-section--lp-main-product .c-product-form__add-to-cart",
      )
      .forEach((button) => {
        if (!targets.includes(button)) targets.push(button);
      });

    return targets;
  }

  #syncOfferAtcHide() {
    const productInView = this.classList.contains("is-atc-visible");

    this.querySelectorAll(".s-lp-custom-header__offer").forEach((offer) => {
      const hideEnabled = this.#getOfferHideWhenAtc(offer);
      const shouldHide = hideEnabled && productInView;
      const inaccessible = shouldHide || !offer.classList.contains("is-scroll-revealed");

      offer.classList.toggle("is-atc-hidden", shouldHide);

      if (inaccessible) {
        offer.setAttribute("aria-hidden", "true");
        offer.setAttribute("inert", "");
      } else {
        offer.removeAttribute("aria-hidden");
        offer.removeAttribute("inert");
      }
    });
  }

  #initAtcSuppression(signal) {
    this.#atcObserver?.disconnect();

    const targets = this.#getAtcHideTargets();

    if (targets.length === 0) {
      if (this.#atcMutationObserver || signal.aborted) return;

      this.#atcMutationObserver = new MutationObserver(() => {
        if (this.#getAtcHideTargets().length === 0) return;

        this.#atcMutationObserver?.disconnect();
        this.#atcMutationObserver = null;
        this.#initAtcSuppression(signal);
      });
      this.#atcMutationObserver.observe(document.body, { childList: true, subtree: true });
      return;
    }

    const visible = new Set();

    this.#atcObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }

        this.classList.toggle("is-atc-visible", visible.size > 0);
        this.#syncOfferAtcHide();
      },
      { threshold: 0 },
    );

    for (const target of targets) {
      this.#atcObserver.observe(target);
    }
  }

  #initScrollCtas(signal) {
    this.querySelectorAll("[data-scroll-to]").forEach((link) => {
      link.addEventListener(
        "click",
        (event) => {
          const targetId = link.dataset.scrollTo;
          if (!targetId) return;

          event.preventDefault();
          event.stopPropagation();

          const target = document.getElementById(targetId) || document.querySelector(`[id$="__${targetId}"]`) || document.querySelector(`[id$="${targetId}"]`);

          target?.scrollIntoView({ behavior: "smooth" });
        },
        { signal },
      );
    });
  }
}

if (!customElements.get("lp-custom-header")) {
  customElements.define("lp-custom-header", LpCustomHeader);
}
